// Ball registry: physics bodies + three.js meshes for every ball a frame can have: the cue ball, the 22 snooker
// balls and the 15 numbered pool-style balls of Any ball (only the balls of the frame's layout are on the table).
// The physics is the event-driven simulation in World.sim (physics/solver.ts). Each ball's PhysicalBody is a small
// view of it with the method names the game, Cue.ts and the e2e scripts used with Box2D (GetPosition,
// GetLinearVelocity, SetLinearVelocity, SetPosition), so none of them had to change.
import Game from "./Game";
import World from "./World";
import {ALL_BALL_IDS, BALL_RADIUS, Positions, Vec, isPoolBall, isRed, poolNumber} from "./shared/rules";
import {Quat, eulerXYZQuat, identityQuat, rollQuat} from "./ballRoll";

declare const THREE: any;

export const BALL_HEIGHT = 7.926842; // table height in the scene

/** A ball's physics body: reads and writes go to World.sim at its current time. */
export interface BallBody {
    GetPosition(): {x: number, y: number};
    GetLinearVelocity(): {x: number, y: number};
    SetLinearVelocity(v: {x: number, y: number}): void;
    SetPosition(p: {x: number, y: number}): void;
    SetAngularVelocity(w: number): void;
    GetUserData(): string;
    destroyed?: boolean;
}

function bodyFor(id: string): BallBody {
    return {
        GetPosition() { const p = World.sim.position(id)!; return {x: p[0], y: p[1]}; },
        GetLinearVelocity() { const v = World.sim.velocity(id); return {x: v[0], y: v[1]}; },
        SetLinearVelocity(v) { World.sim.setVelocity(id, [v.x, v.y]); },
        SetPosition(p) { World.sim.setPosition(id, [p.x, p.y]); },
        SetAngularVelocity() { /* no spin yet */ },
        GetUserData() { return id; },
    };
}

export interface Ball {
    id: string;
    PhysicalBody: any;
    Rendered: any;
    Killed: boolean;
    /** the mesh's orientation: it rolls as the ball moves (ballRoll.ts) */
    orientation?: Quat;
}

const COLOURS: Record<string, number> = {
    cue: 0xf4f1e8, red: 0xb5121b, yellow: 0xf2c200, green: 0x0b7a3b, brown: 0x7a4a1c,
    blue: 0x1446a8, pink: 0xf28cb1, black: 0x151515,
};

// The numbered set (Any ball): the traditional colours, 1-8 solids and 9-15 stripes of the colour of n - 8.
export const POOL_COLOURS: Record<number, string> = {
    1: "#f5c400", 2: "#1b4fb8", 3: "#d0202a", 4: "#5a2a86", 5: "#f07a14", 6: "#0c7a3c", 7: "#7b1d24", 8: "#121212",
};
export const poolColour = (n: number) => POOL_COLOURS[n > 8 ? n - 8 : n];
export const isStripe = (n: number) => n >= 9;

/**
 * The texture of a numbered ball, laid out as the sphere's UVs are (equirectangular: x = longitude, y = latitude,
 * so a circle near the equator stays round): a solid colour, or white with a colour band for a stripe, and the
 * number in a white disc on both sides.
 */
function poolTexture(n: number) {
    const w = 512, h = 256;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const c = canvas.getContext("2d")!;
    c.fillStyle = isStripe(n) ? "#f4f1e8" : poolColour(n);
    c.fillRect(0, 0, w, h);
    if (isStripe(n)) {
        c.fillStyle = poolColour(n);
        c.fillRect(0, h * 0.27, w, h * 0.46);
    }
    for (const x of [w * 0.25, w * 0.75]) {
        c.fillStyle = "#f4f1e8";
        c.beginPath(); c.arc(x, h / 2, h * 0.16, 0, Math.PI * 2); c.fill();
        c.fillStyle = "#111";
        c.font = `bold ${Math.round(h * (n >= 10 ? 0.15 : 0.18))}px Arial, Helvetica, sans-serif`;
        c.textAlign = "center"; c.textBaseline = "middle";
        c.fillText(String(n), x, h / 2 + 1);
    }
    const texture = new THREE.Texture(canvas);
    texture.needsUpdate = true;
    return texture;
}

let geometry: any = null;
// The textured (numbered) balls have their own sphere: three.js r58 sets up a geometry's buffers (with or without
// UVs) for the first material it is drawn with, so the plain balls' sphere would draw the textures without UVs.
let texturedGeometry: any = null;
let segments: [number, number] = [32, 20];
const materials: Record<string, any> = {};

function meshFor(id: string) {
    if (!geometry) geometry = new THREE.SphereGeometry(BALL_RADIUS, segments[0], segments[1]);
    if (!texturedGeometry) texturedGeometry = new THREE.SphereGeometry(BALL_RADIUS, segments[0], segments[1]);
    const key = isRed(id) ? "red" : id;
    if (!materials[key]) {
        materials[key] = isPoolBall(id)
            ? new THREE.MeshPhongMaterial({map: poolTexture(poolNumber(id)), color: 0xffffff, ambient: 0xffffff, specular: 0x777777, shininess: 90})
            : new THREE.MeshPhongMaterial({color: COLOURS[key], ambient: COLOURS[key], specular: 0x777777, shininess: 90});
    }
    // no shadow map: the shadow under each ball is a soft blob on the cloth (ballShadows.ts)
    const mesh = new THREE.Mesh(isPoolBall(id) ? texturedGeometry : geometry, materials[key]);
    // The orientation is a quaternion (the balls roll, ballRoll.ts); Balls sets it from Ball.orientation.
    mesh.useQuaternion = true;
    return mesh;
}

/** Where a ball starts: each numbered ball turned its own way, so the numbers don't all face one side. */
function startOrientation(id: string): Quat {
    if (!isPoolBall(id)) return identityQuat();
    const n = poolNumber(id);
    return eulerXYZQuat(((n * 7) % 5 - 2) * 0.18, n * 2.39996, 0);   // golden angle steps
}

function applyOrientation(ball: Ball) {
    const q = ball.orientation || identityQuat();
    ball.Rendered.quaternion.set(q.x, q.y, q.z, q.w);
}

/** Moves the mesh to (x, z) on the cloth, turning it as the ball rolls there from where it was drawn last. */
function rollMeshTo(ball: Ball, x: number, z: number) {
    const pos = ball.Rendered.position;
    const next = rollQuat(ball.orientation || identityQuat(), x - pos.x, z - pos.z, BALL_RADIUS);
    if (next !== ball.orientation) {
        ball.orientation = next;
        applyOrientation(ball);
    }
    pos.x = x;
    pos.z = z;
}

const Balls = {
    list: [] as Ball[],
    byId: {} as Record<string, Ball>,

    /** Creates all balls (called once from Game.InitScene). */
    init(positions: Positions) {
        Balls.list = [];
        Balls.byId = {};
        for (const id of ALL_BALL_IDS) {
            // The cue ball keeps the object Game.Scenario.WhiteBall so existing code (cue, camera) works.
            const ball: Ball = id === "cue" ? Game.Scenario.WhiteBall : {id, PhysicalBody: null, Rendered: null, Killed: true} as Ball;
            ball.id = id;
            ball.Killed = true;
            ball.Rendered = meshFor(id);
            ball.orientation = startOrientation(id);
            applyOrientation(ball);
            ball.Rendered.position.set(0, BALL_HEIGHT, 0);
            Balls.list.push(ball);
            Balls.byId[id] = ball;
        }
        Balls.applyLayout(positions);
    },

    put(ball: Ball, p: Vec) {
        if (ball.Killed || !ball.PhysicalBody) {
            ball.PhysicalBody = bodyFor(ball.id);
            Game.Scenario.scene.add(ball.Rendered);
            ball.Killed = false;
        }
        World.sim.place(ball.id, [p[0], p[1]]);   // at rest
        ball.Rendered.position.x = p[0];
        ball.Rendered.position.z = p[1];
        ball.Rendered.position.y = BALL_HEIGHT;
    },

    /** Take a ball off the table (potted). The body's last position stays readable. */
    kill(ball: Ball) {
        if (ball.Killed) return;
        Game.Scenario.scene.remove(ball.Rendered);
        if (ball.PhysicalBody) {
            const last = ball.PhysicalBody.GetPosition();
            const x = last.x, y = last.y;
            World.sim.remove(ball.id);
            // keep a plain position object so the camera can still follow a potted cue ball
            ball.PhysicalBody = {
                GetPosition: () => ({x, y}),
                GetLinearVelocity: () => ({x: 0, y: 0}),
                SetLinearVelocity: () => undefined,
                SetPosition: () => undefined,
                SetAngularVelocity: () => undefined,
                destroyed: true,
            };
        }
        ball.Killed = true;
    },

    /** Snap the table to a layout: create, move or remove balls; everything stops. */
    applyLayout(positions: Positions) {
        for (const ball of Balls.list) {
            const p = positions[ball.id];
            if (p) {
                if (ball.PhysicalBody && ball.PhysicalBody.destroyed) ball.PhysicalBody = null;
                Balls.put(ball, p);
            } else {
                Balls.kill(ball);
            }
        }
    },

    /**
     * Teleport without stopping/creating (spectator playback). `roll`: the ball got there by rolling (the replay's
     * frames), so the mesh turns with the move; otherwise (ball in hand, test hooks) it just moves.
     */
    show(ball: Ball, p: Vec, roll = false) {
        if (ball.Killed) {
            if (ball.PhysicalBody && ball.PhysicalBody.destroyed) ball.PhysicalBody = null;
            Balls.put(ball, p);
            return;
        }
        World.sim.place(ball.id, [p[0], p[1]]);   // moved, at rest
        if (roll) {
            rollMeshTo(ball, p[0], p[1]);
        } else {
            ball.Rendered.position.x = p[0];
            ball.Rendered.position.z = p[1];
        }
    },

    /** Per rendered frame of the physics: the mesh follows its body, rolling (Game.UpdateState). */
    follow(ball: Ball) {
        const p = ball.PhysicalBody.GetPosition();
        rollMeshTo(ball, p.x, p.y);
    },

    positions(): Positions {
        const out: Positions = {};
        for (const ball of Balls.list) {
            if (ball.Killed) continue;
            const p = ball.PhysicalBody.GetPosition();
            out[ball.id] = [Math.round(p.x * 10000) / 10000, Math.round(p.y * 10000) / 10000];
        }
        return out;
    },

    onTable(): Ball[] {
        return Balls.list.filter(b => !b.Killed);
    },

    /**
     * Graphics quality: the spheres' segments (quality.ts ballSegments). Each ball gets a new mesh with the new
     * sphere, in place of the old one (three.js r58 can't swap a drawn mesh's geometry); positions and orientations
     * are kept.
     */
    setSegments(seg: [number, number]) {
        if (seg[0] === segments[0] && seg[1] === segments[1]) return;
        segments = seg;
        const old = geometry, oldTextured = texturedGeometry;
        geometry = null;
        texturedGeometry = null;
        for (const ball of Balls.list) {
            const mesh = meshFor(ball.id);
            mesh.position.copy(ball.Rendered.position);
            const q = ball.orientation || identityQuat();
            mesh.quaternion.set(q.x, q.y, q.z, q.w);
            if (!ball.Killed) {
                Game.Scenario.scene.remove(ball.Rendered);
                Game.Scenario.scene.add(mesh);
            }
            ball.Rendered = mesh;
        }
        for (const g of [old, oldTextured]) if (g && g.dispose) g.dispose();
    },
};

export default Balls;
